"use client";

import * as React from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { DRAG_DROP_PASTE } from "@lexical/rich-text";
import { mergeRegister } from "@lexical/utils";
import { COMMAND_PRIORITY_HIGH, PASTE_COMMAND } from "lexical";

const LEXICAL_CLIPBOARD_MIME = "application/x-lexical-editor";

function transferTypesIncludeLexical(dt: DataTransfer): boolean {
  const types = dt.types;
  if (types == null) return false;
  return Array.from(types).includes(LEXICAL_CLIPBOARD_MIME);
}

/** 含 Lexical JSON 类型或由 Clipboard API 写入但同步 `getData` 仍为空时，不按「纯图文件」拦截。 */
function hasOurLexicalClipboardPayload(dt: DataTransfer, namespace: string): boolean {
  if (!transferTypesIncludeLexical(dt)) return false;
  try {
    const raw = dt.getData(LEXICAL_CLIPBOARD_MIME);
    if (raw) {
      const p = JSON.parse(raw) as { namespace?: string; nodes?: unknown[] };
      return p.namespace === namespace && Array.isArray(p.nodes);
    }
    return true;
  } catch {
    return true;
  }
}

function clipboardHasImageFile(dt: DataTransfer): boolean {
  for (const f of Array.from(dt.files ?? [])) {
    if (f.type.startsWith("image/")) return true;
  }
  for (const item of Array.from(dt.items ?? [])) {
    if (item.kind === "file" && item.type.startsWith("image/")) return true;
  }
  return false;
}

/**
 * 禁止把剪贴板/拖拽中的二进制图片文件插入编辑器；图片块仅允许通过 URL（及 Lexical 块 JSON）复制粘贴。
 * 带本编辑器 `application/x-lexical-editor` 时不拦截，以便图片块原样复制。
 */
export function RejectBinaryImagePastePlugin() {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          if (!editor.isEditable()) return false;
          if (!(event instanceof ClipboardEvent) || event.clipboardData == null) return false;
          const dt = event.clipboardData;
          if (hasOurLexicalClipboardPayload(dt, editor._config.namespace)) return false;
          if (!clipboardHasImageFile(dt)) return false;
          const html = dt.getData("text/html").trim();
          const plain = dt.getData("text/plain").trim();
          if (html.length > 0 || plain.length > 0) return false;
          event.preventDefault();
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        DRAG_DROP_PASTE,
        (files: File[]) => {
          if (!editor.isEditable()) return false;
          if (!files.some((f) => f.type.startsWith("image/"))) return false;
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [editor]);

  return null;
}
